import React, { useState } from 'react';
import { useSEO } from '../hooks/useSEO';
import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';
import { EDUCATION_UNITS } from '../data/mock';
import { MapPin, Phone, Mail, Check, ArrowUpRight, Building2, GraduationCap, Calendar } from 'lucide-react';
import { EditRegion } from '../components/AdminEditBar';
import { InlineText, InlineImage } from '../components/InlineEditable';
import EditableList from '../components/EditableList';
import GoogleMapEmbed from '../components/GoogleMapEmbed';

const TAG_ICON = {
  'Genel Merkez': Building2,
  'Öğrenci Yurdu': GraduationCap,
  'Eğitim Merkezi': Building2,
};

export default function EducationUnits() {
  useSEO('units');
  const [active, setActive] = useState(EDUCATION_UNITS[0]);
  const Icon = TAG_ICON[active.tag] || Building2;

  return (
    <EditRegion label="Eğitim Birimleri" to="/admin/static-pages">
      <>
      <PageHero slug="units"
        kicker="Eğitim Destek Birimlerimiz"
        title={<>İstanbul'un <span className="italic text-coral-200">yedi farklı semtinde</span><br />sizinleyiz.</>}
        text="Mecidiyeköy'deki genel merkezimizden Kadıköy yurduna, Beyoğlu'ndan Nuruosmaniye'ye kadar; 7 eğitim destek birimimizle şehrin her yanında bursiyerlerimizin yanındayız."
      />

      {/* Stats strip */}
      <section className="bg-cream-200 border-y border-ink-100">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { n: '7', l: 'Aktif Birim' },
            { n: '5', l: 'İlçe' },
            { n: '96', l: 'Yurt Kapasitesi' },
            { n: '1.200+', l: 'Yıllık Ziyaretçi' },
          ].map((s, i) => {
            const idx = i + 1;
            return (
              <div key={i} className="text-center md:text-left">
                <InlineText settingKey={`units_stat_${idx}_n`} fallback={String(s.n)} as="div"
                  className="font-display text-4xl md:text-5xl text-ink-600 tabular" />
                <InlineText settingKey={`units_stat_${idx}_l`} fallback={s.l} as="div"
                  className="text-xs uppercase tracking-[0.2em] text-ink-400 mt-2" />
              </div>
            );
          })}
        </div>
      </section>

      {/* Interactive unit switcher */}
      <section className="bg-cream">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-24">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <div>
              <InlineText settingKey="units_switcher_kicker" fallback="Merkezlerimiz" as="div"
                className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-semibold" />
              <InlineText settingKey="units_switcher_title" fallback="Yakından tanıyın." as="h2"
                multiline className="font-display text-4xl md:text-5xl mt-3 text-ink-600 leading-[1.05]" />
            </div>
            <div className="text-sm text-ink-400 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              <InlineText settingKey="units_switcher_note" fallback="Tüm merkezlerimiz İstanbul'da" as="span" />
            </div>
          </div>

          {/* Chip filters */}
          <div className="flex flex-wrap gap-2 mb-8">
            {EDUCATION_UNITS.map((u) => (
              <button
                key={u.id}
                onClick={() => setActive(u)}
                className={`px-5 h-11 rounded-full text-sm font-medium transition-all ${active.id === u.id ? 'bg-ink-600 text-cream shadow-lg shadow-ink-900/20' : 'bg-white border border-ink-100 text-ink-500 hover:border-ink-300'}`}
              >
                {u.district}
              </button>
            ))}
          </div>

          {/* Feature split view */}
          <div className="grid lg:grid-cols-12 gap-6 lg:gap-10 items-start">
            {/* Left: photo showcase */}
            <div className="lg:col-span-7">
              <div className="relative rounded-[32px] overflow-hidden shadow-2xl shadow-ink-900/20 aspect-[4/3] bg-ink-50">
                <InlineImage
                  key={active.id}
                  settingKey={`unit_${active.id}_image`}
                  fallback={active.image}
                  alt={active.name}
                  className="w-full h-full object-cover fade-up"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-900/80 via-ink-900/10 to-transparent pointer-events-none" />
                <div className="absolute top-6 left-6 right-6 flex items-start justify-between gap-3 pointer-events-none">
                  <span className="inline-flex items-center gap-2 bg-white/95 backdrop-blur text-ink-600 rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-widest">
                    <Icon className="w-4 h-4 text-gold-500" /> {active.tag}
                  </span>
                  <span className="inline-flex items-center gap-1.5 bg-gold/95 text-ink-700 rounded-full px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest">
                    <Calendar className="w-3.5 h-3.5" /> {active.since}'den beri
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-8 md:p-10 text-cream pointer-events-none">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-2 font-semibold inline-flex items-center gap-2">
                    <MapPin className="w-3.5 h-3.5" /> {active.district}
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl lg:text-[44px] leading-[1.05]">
                    <InlineText settingKey={`unit_${active.id}_name`} fallback={active.name} as="span" multiline />
                  </h3>
                </div>
              </div>

              {/* Thumb strip */}
              <div className="mt-4 grid grid-cols-7 gap-2">
                {EDUCATION_UNITS.map((u) => (
                  <button
                    key={u.id}
                    onClick={() => setActive(u)}
                    className={`relative rounded-xl overflow-hidden aspect-square transition-all ${active.id === u.id ? 'ring-2 ring-ink-600 ring-offset-2 ring-offset-cream' : 'opacity-60 hover:opacity-100'}`}
                    aria-label={u.district}
                  >
                    <img src={u.image} alt={u.district} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>

            {/* Right: detail panel */}
            <div className="lg:col-span-5">
              <div className="bg-white rounded-3xl border border-ink-100 p-8 md:p-10 shadow-xl shadow-ink-900/5">
                <InlineText settingKey={`unit_${active.id}_description`} fallback={active.description} as="p"
                  multiline className="text-ink-500 leading-relaxed" />

                <EditableList
                  key={active.id}
                  settingKey={`unit_${active.id}_features_list`}
                  defaultItems={active.features}
                  addLabel="Yeni özellik ekle"
                  listAs="div"
                  className="mt-6 grid grid-cols-1 gap-2"
                  itemClassName="pr-16"
                  renderItem={(text) => (
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-cream-200 text-sm text-ink-600">
                      <div className="w-6 h-6 rounded-full bg-gold/20 text-gold-500 grid place-items-center shrink-0"><Check className="w-3.5 h-3.5" /></div>
                      <span className="flex-1 whitespace-pre-wrap">{text}</span>
                    </div>
                  )}
                />

                <div className="mt-8 h-px bg-ink-100" />

                {/* Google Map — per unit */}
                <div className="mt-6">
                  <GoogleMapEmbed
                    key={active.id}
                    settingKey={`unit_${active.id}_map_url`}
                    fallback=""
                    height={220}
                  />
                </div>

                <div className="mt-6 space-y-3 text-sm">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-ink-50 grid place-items-center text-ink-600 shrink-0"><MapPin className="w-4 h-4" /></div>
                    <div className="flex-1 min-w-0"><div className="text-[10px] uppercase tracking-widest text-ink-400 font-semibold">Adres</div>
                      <InlineText settingKey={`unit_${active.id}_address`} fallback={active.address} as="div"
                        multiline className="text-ink-600 mt-0.5" />
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-ink-50 grid place-items-center text-ink-600 shrink-0"><Phone className="w-4 h-4" /></div>
                    <div className="flex-1 min-w-0"><div className="text-[10px] uppercase tracking-widest text-ink-400 font-semibold">Telefon</div>
                      <InlineText settingKey={`unit_${active.id}_phone`} fallback={active.phone} as="div"
                        className="text-ink-600 mt-0.5" />
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-ink-50 grid place-items-center text-ink-600 shrink-0"><Mail className="w-4 h-4" /></div>
                    <div className="flex-1 min-w-0"><div className="text-[10px] uppercase tracking-widest text-ink-400 font-semibold">E-posta</div>
                      <InlineText settingKey={`unit_${active.id}_email`} fallback={active.email} as="div"
                        className="text-ink-600 mt-0.5 break-all" />
                    </div>
                  </div>
                </div>

                {active.id === 'mecidiyekoy' && (
                  <Link to="/iletisim" className="mt-8 inline-flex items-center gap-2 bg-ink-600 text-cream rounded-full px-6 h-12 font-semibold hover:bg-ink-500 transition-colors w-full justify-center" data-testid="visit-request-btn">
                    Ziyaret Talebi Gönder <ArrowUpRight className="w-4 h-4" />
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* All units bento grid */}
      <section className="bg-cream-200">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-28">
          <div className="max-w-3xl mb-12">
            <InlineText settingKey="units_grid_kicker" fallback="Tüm Birimlerimiz" as="div"
              className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-semibold" />
            <InlineText settingKey="units_grid_title" fallback="Yedi merkez, tek misyon." as="h2"
              multiline className="font-display text-4xl md:text-5xl mt-3 text-ink-600 leading-[1.05]" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {EDUCATION_UNITS.map((u, i) => {
              const TagIcon = TAG_ICON[u.tag] || Building2;
              const featured = i === 0; // Merkez öne çıkar
              return (
                <article
                  key={u.id}
                  className={`group bg-white rounded-3xl overflow-hidden border border-ink-100 hover:shadow-2xl hover:shadow-ink-900/10 transition-all hover:-translate-y-1 ${featured ? 'md:col-span-2 lg:col-span-2 md:row-span-1' : ''}`}
                >
                  <div className={`relative overflow-hidden bg-ink-50 ${featured ? 'aspect-[16/9]' : 'aspect-[4/3]'}`}>
                    <InlineImage settingKey={`unit_${u.id}_image`} fallback={u.image} alt={u.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute top-4 left-4 pointer-events-none">
                      <span className="inline-flex items-center gap-1.5 bg-white/95 backdrop-blur text-ink-600 rounded-full px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest">
                        <TagIcon className="w-3 h-3 text-gold-500" /> {u.tag}
                      </span>
                    </div>
                  </div>
                  <div className="p-6 md:p-7">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-coral-300 font-semibold inline-flex items-center gap-1.5">
                      <MapPin className="w-3 h-3" />
                      <InlineText settingKey={`unit_${u.id}_district`} fallback={u.district} as="span" />
                    </div>
                    <InlineText settingKey={`unit_${u.id}_name`} fallback={u.name} as="h3" multiline
                      className={`font-display mt-2 text-ink-600 leading-tight ${featured ? 'text-2xl md:text-3xl' : 'text-xl md:text-2xl'}`} />
                    <InlineText settingKey={`unit_${u.id}_summary`} fallback={u.summary} as="p" multiline
                      className="mt-3 text-sm text-ink-500 leading-relaxed line-clamp-3" />
                    <button
                      onClick={() => { setActive(u); window.scrollTo({ top: 600, behavior: 'smooth' }); }}
                      className="mt-5 inline-flex items-center gap-2 text-ink-600 font-medium text-sm group-hover:gap-3 transition-all"
                    >
                      Detayları gör <ArrowUpRight className="w-4 h-4" />
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-cream">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 md:py-20">
          <div className="relative overflow-hidden rounded-[32px] bg-ink-600 text-cream p-10 md:p-14 grid md:grid-cols-2 gap-8 items-center">
            <div className="absolute -top-24 -right-24 w-[400px] h-[400px] rounded-full bg-gold/15 blur-3xl pointer-events-none" />
            <div className="relative">
              <InlineText settingKey="units_cta_kicker" fallback="Bize Gelin" as="div"
                className="text-[11px] tracking-[0.3em] uppercase text-gold font-semibold" />
              <InlineText settingKey="units_cta_title" fallback="Merkezlerimizi yerinde görmek ister misiniz?" as="h3"
                multiline className="font-display text-3xl md:text-4xl lg:text-5xl mt-3 leading-tight" />
              <InlineText settingKey="units_cta_text" fallback="Her bir birimimiz, bağışçılarımıza ve gönüllülerimize her zaman açıktır. Size en yakın merkezimizi ziyaret ederek bursiyerlerimizle tanışabilirsiniz." as="p"
                multiline className="mt-4 text-cream/75" />
            </div>
            <div className="relative flex md:justify-end">
              <Link to="/iletisim" className="inline-flex items-center gap-2 bg-gold text-ink-700 rounded-full px-6 h-14 font-semibold hover:bg-gold-400 transition-colors">
                Ziyaret Planla <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
    </EditRegion>
  );
}
